import { authenticatedFetch } from '../utils/api';
import { API_HOST } from '../utils/constants';

const officialProviders = new Set(['volcengine_sd25', 'byteplus_sd25']);
const containsControl = (value) => Array.from(value).some((character) => {
  const code = character.charCodeAt(0);
  return code <= 31 || (code >= 127 && code <= 159);
});
const libraryPath = (provider) => {
  if (!officialProviders.has(provider)) throw new Error('请选择国内或海外官方素材库');
  return `${API_HOST}/api/admin/modelark/libraries/${provider}`;
};

export const readOfficialLibraryGroup = (provider, signal) => (
  authenticatedFetch(`${libraryPath(provider)}/group-info`, { signal })
);

export function buildOfficialLibraryVerification(provider, assetId, snapshot) {
  if (provider !== 'volcengine_sd25') throw new Error('本站核验目前仅支持国内 Seedance 官方素材库');
  if (!snapshot || snapshot.provider !== provider || typeof snapshot.revision !== 'string' || !snapshot.revision.trim()) {
    throw new Error('素材库配置尚未读取，请读取本站配置后重试');
  }
  if (!/^group-[A-Za-z0-9_-]{1,194}$/.test(snapshot.groupId || '')) throw new Error('请先绑定已有素材组，再核验');
  const inputId = String(assetId || '').trim();
  const normalizedId = inputId.replace(/^asset:\/\//, '');
  if (inputId && !/^asset-[A-Za-z0-9_-]{1,194}$/.test(normalizedId)) {
    throw new Error('请填写有效的素材 ID（asset- 开头），不是图片 URL 或素材组 ID');
  }
  return { expectedRevision: snapshot.revision, ...(normalizedId ? { assetId: normalizedId } : {}) };
}

// Explicit read-only vendor inspection; never create assets, retry, or switch libraries.
export const verifyOfficialLibraryGroup = (provider, assetId, snapshot, signal) => {
  const body = buildOfficialLibraryVerification(provider, assetId, snapshot);
  return authenticatedFetch(`${libraryPath(provider)}/verify`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal,
  });
};

export function formatOfficialLibraryError(failure, action) {
  const conflict = failure?.status === 409 || failure?.code === 409;
  const message = String(failure?.message || (conflict ? '素材库配置已变化，或上次创建结果需要核查' : '操作未完成')).replace(/[。；\s]+$/u, '');
  if (conflict) return `${message}。请读取最新状态后重新确认；已创建的素材组请直接绑定。`;
  return `${message}${action !== 'bind' ? '。请查看最近一次创建记录；若已有素材组，请直接绑定该 ID。' : ''}`;
}

export function buildOfficialLibraryMutation(provider, action, values, snapshot) {
  libraryPath(provider);
  if (!snapshot || snapshot.provider !== provider || snapshot.revision == null) {
    throw new Error('素材库配置尚未读取，请刷新后重试');
  }
  if (snapshot.bindingManagedByConfiguration && action !== 'create-candidate') {
    throw new Error('当前素材库由 application.properties / 环境配置固定，请修改配置文件；后台旧绑定未修改');
  }
  const projectName = String(values.projectName || '').trim();
  if (!projectName || projectName.length > 200 || containsControl(projectName)) throw new Error('请填写有效的官方项目空间');
  const common = { projectName, expectedRevision: snapshot.revision };
  if (action === 'bind' || action === 'bind-candidate') {
    const groupId = String(values.groupId || '').trim();
    if (!/^group-[A-Za-z0-9_-]{1,194}$/.test(groupId)) throw new Error('请填写有效的已有素材组 ID（group- 开头）');
    if (action === 'bind-candidate') {
      const attempt = snapshot.creationAttempt;
      if (provider !== 'volcengine_sd25' || attempt?.mode !== 'candidate'
          || attempt.createdGroupId !== groupId || attempt.projectName !== projectName) {
        throw new Error('候选素材组记录或目标已变化，请关闭窗口、读取最新记录后重新选择候选；其他目标请使用“更换绑定”');
      }
      if (typeof snapshot.creationRevision !== 'string' || !snapshot.creationRevision.trim()) {
        throw new Error('候选创建记录尚未读取，请读取本站配置后重试');
      }
      return { ...common, groupId, expectedCreationRevision: snapshot.creationRevision };
    }
    return { ...common, groupId };
  }
  const retry = action === 'retry-create';
  const candidate = action === 'create-candidate';
  if (action !== 'create' && !retry && !candidate) throw new Error('不支持的素材库操作');
  const attempt = snapshot.creationAttempt;
  if (candidate) {
    if (provider !== 'volcengine_sd25' || snapshot.canCreateCandidate !== true || attempt?.state === 'pending') {
      throw new Error('当前不能创建候选素材组，请读取本站配置后确认');
    }
    if (typeof snapshot.creationRevision !== 'string' || !snapshot.creationRevision.trim()) {
      throw new Error('创建记录尚未读取，请读取本站配置后重试');
    }
    if (values.confirmCreateAdditional !== true) throw new Error('请先确认另建素材组可能重复，且不会切换或删除当前绑定');
  } else if (provider !== 'volcengine_sd25' || snapshot.groupId || attempt?.createdGroupId
      || (retry ? snapshot.canRetryCreate !== true || !attempt?.revision || attempt.state !== 'unknown'
        : snapshot.canCreate !== true || snapshot.createBlocked === true || ['pending', 'unknown', 'created'].includes(attempt?.state || snapshot.creationState))) {
    throw new Error('当前库不能新建，请刷新配置或绑定已存在的素材组');
  }
  if (retry && values.confirmRetryAfterUnknown !== true) throw new Error('请先确认已阅读本站创建记录，并知悉再次创建可能产生重复素材组');
  const name = String(values.name || '').trim();
  const description = String(values.description || '').trim();
  if (!name || name.length > 64 || containsControl(name)) throw new Error('素材组名称为 1–64 个字符，不能包含控制字符');
  if (description.length > 300 || containsControl(description)) throw new Error('描述最多 300 个字符，不能包含控制字符');
  return { ...common, name, description, ...(candidate ? { expectedCreationRevision: snapshot.creationRevision, confirmCreateAdditional: true }
    : retry ? { expectedCreationRevision: attempt.revision, confirmRetryAfterUnknown: true } : {}) };
}

// These writes are explicit, single attempts. A timeout must be inspected rather
// than automatically replayed, because the official group may already exist.
export const saveOfficialLibraryGroup = (provider, action, values, snapshot) => {
  const body = buildOfficialLibraryMutation(provider, action, values, snapshot);
  const route = { create: 'create-group', bind: 'bind-group', 'bind-candidate': 'bind-group', 'retry-create': 'retry-create-group', 'create-candidate': 'create-candidate-group' }[action];
  return authenticatedFetch(`${libraryPath(provider)}/${route}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
};
